<div class="guide-page">

  <!-- Header -->
  <div class="guide-header">
    <div>
      <h1>Owner Statement Production — How-To Guide</h1>
      <p class="subtitle">Read this before you start. Bookmark this page. Updated 2026-05-12.</p>
    </div>
    <div class="header-actions">
      <button class="btn-print" (click)="printGuide()">🖨 Print</button>
    </div>
  </div>

  <!-- TOC -->
  <nav class="toc">
    <strong>Jump to:</strong>
    <a (click)="scrollTo('big-picture')">1. Big Picture</a>
    <a (click)="scrollTo('calendar')">2. Monthly Calendar</a>
    <a (click)="scrollTo('dashboard')">3. Dashboard Tiles</a>
    <a (click)="scrollTo('statuses')">4. Statement Statuses</a>
    <a (click)="scrollTo('regenerate')">5. Regenerate vs Generate</a>
    <a (click)="scrollTo('payments')">6. Payments &amp; NACHA</a>
    <a (click)="scrollTo('manual-check')">7. Manual Check Payments</a>
    <a (click)="scrollTo('newsletter')">8. Newsletter / CMS Letter</a>
    <a (click)="scrollTo('smoke-test')">9. Smoke Test &amp; Bulk Email</a>
    <a (click)="scrollTo('reconciliation')">10. Wire Reconciliation</a>
    <a (click)="scrollTo('tax-filing')">11. Tax Filing (GET / TAT / MCTAT)</a>
    <a (click)="scrollTo('troubleshooting')">12. Troubleshooting</a>
    <a (click)="scrollTo('glossary')">13. Glossary</a>
  </nav>

  <!-- Section 1 -->
  <section id="big-picture">
    <h2>1. The Big Picture</h2>
    <p>
      Every month KBM produces an <strong>Owner Statement</strong> for every active property — a PDF showing the
      reservations, expenses, management fees, and the bottom-line payment owed to the owner.
      Owners must receive their statement and their payment by the <strong>12th</strong> of the
      following month (or the prior business day if the 12th is a weekend / holiday).
    </p>
    <p>The flow is:</p>
    <ol class="big-flow">
      <li><strong>Data In:</strong> Reservations + ledger items + expenses are entered/imported throughout the month.</li>
      <li><strong>Generate:</strong> Around the 25th-28th of the month, a <em>bulk generate</em> creates a Draft statement for every active property.</li>
      <li><strong>Top-Up:</strong> As more data lands (late expenses, last-minute bookings, OTA imports), the Dashboard shows what's missing — click the buttons to <em>regenerate</em>.</li>
      <li><strong>Review:</strong> Bookkeeping advances each statement through the review stages (Need Attention → RFR1 → RFR2 → RFR3 → Approved).</li>
      <li><strong>Newsletter:</strong> Sarah creates one CMS letter per development for the period, wires it to the right developmentIds via the CMS Statement Letters page, and saves as <strong>PUBLISHED</strong>. See <a (click)="scrollTo('newsletter')">Section 8</a>.</li>
      <li><strong>Publish:</strong> Sarah / Jacki / Justin publish statements — this freezes them.</li>
      <li><strong>Smoke Test:</strong> Bulk-email <em>only</em> HKH-236 + HKK-334 first. Verify newsletter appears in body and all co-owners receive it. See <a (click)="scrollTo('smoke-test')">Section 9</a>.</li>
      <li><strong>Email:</strong> Bulk-email all remaining Published statements to owners.</li>
      <li><strong>NACHA Distribution:</strong> Generate the distribution NACHA file, Jacki uploads to Bank of Hawaii. After settlement, statements auto-mark PAID. Owners without bank info get manual checks.</li>
      <li><strong>Reconcile:</strong> Once BOH posts the wire, cross-check Bank amount vs Wiki "payment to owner" per stmt. Resolve any over/underpayments through next-month adjustments or supplemental ACH. See <a (click)="scrollTo('reconciliation')">Section 10</a>.</li>
      <li><strong>Tax Filing (Day +8):</strong> Divya generates the tax-side NACHA (GET / TAT / MCTAT / OTAT / HCTAT) by the 20th of the month for HI taxes collected during the prior calendar month. See <a (click)="scrollTo('tax-filing')">Section 11</a>.</li>
    </ol>
    <div class="callout">
      <strong>The Dashboard's job</strong> is to show you what work remains in plain numbers, with one-click buttons to do that work. <strong>Green dashboard = ready to publish &amp; pay.</strong>
    </div>
  </section>

  <!-- Section 2 -->
  <section id="calendar">
    <h2>2. Monthly Calendar</h2>
    <p>This is the typical timeline for the <strong>March</strong> statement (paid May 12). Adjust dates by month.</p>
    <table class="calendar-table">
      <thead>
        <tr><th>When</th><th>Who</th><th>What</th></tr>
      </thead>
      <tbody>
        <tr><td>Throughout March</td><td>Front Office / Ops</td><td>Reservations and guest add-ons created. Ledger rows auto-lock at creation (as of 2026-04-26).</td></tr>
        <tr><td>Throughout March</td><td>Bookkeeping</td><td>Expenses entered in str_expense (manual) or arrive from Breezeway (auto). Each expense has a Statement Billed Date.</td></tr>
        <tr><td>Mar 28 – Apr 5</td><td>Bookkeeping</td><td>Expenses are reviewed and advanced to <strong>REVIEWED</strong> status (status 6). Only REVIEWED expenses appear on statements.</td></tr>
        <tr><td>Apr 1 – Apr 5</td><td>Anyone with admin access</td><td><strong>Bulk Generate Statements</strong> for the March period. This creates one Draft per active property.</td></tr>
        <tr><td>Apr 5 – Apr 8</td><td>Bookkeeping</td><td>Click "Regenerate Statements" / "Regenerate Expenses" on the Dashboard whenever new data arrives. Repeat until everything is on the statement.</td></tr>
        <tr><td>Apr 5 – Apr 9</td><td>Sarah / Jacki / Justin</td><td>Review statements through the status ladder. Spot-check totals. Move to <strong>APPROVED</strong>.</td></tr>
        <tr><td>Apr 9 – Apr 10</td><td>Sarah / Jacki / Justin</td><td>Publish all approved statements (status → 7). After this, the statement is frozen.</td></tr>
        <tr><td>Apr 10</td><td>Bookkeeping</td><td>Bulk-email all Published statements to owners. Confirmation phrase required.</td></tr>
        <tr><td>Apr 10 – Apr 11</td><td>Jacki</td><td>Generate NACHA Distribution file. Upload to BOH. Jacki approves in BOH portal.</td></tr>
        <tr><td>Apr 12</td><td>Bank of Hawaii</td><td>ACH payments hit owner accounts. Statements auto-mark <strong>PAID</strong> overnight.</td></tr>
        <tr><td>Apr 12+</td><td>Bookkeeping</td><td>Cut manual checks for owners without bank info. Mark each statement <strong>MANUAL_PAID</strong> with check #.</td></tr>
      </tbody>
    </table>
  </section>

  <!-- Section 3 -->
  <section id="dashboard">
    <h2>3. Dashboard Tiles — In Plain English</h2>
    <p>Each tile on the dashboard tracks one thing. <strong>Goal: every tile is green before you publish.</strong></p>

    <div class="tile-help">
      <h3>🎯 Reservations Ready to Add</h3>
      <p><strong>What it means:</strong> Bookings that fall in this statement period but are NOT YET on the owner statement.</p>
      <p><strong>Why this happens:</strong> Statements are usually generated mid-month. Any booking made after that point is missing until you regenerate. This is normal.</p>
      <p><strong>Colors:</strong> Green = 0. Yellow = some waiting (not a problem, just click).</p>
      <p><strong>What to do:</strong> Click the yellow <em>Regenerate Statements</em> button on the tile. Pulls them in. Published statements are NEVER touched.</p>
    </div>

    <div class="tile-help">
      <h3>💵 Expenses Ready to Add</h3>
      <p><strong>What it means:</strong> Reviewed expenses (work orders, vendor charges) in this period that are NOT YET on the statement.</p>
      <p><strong>Why this happens:</strong> Expenses must be in <strong>REVIEWED</strong> status to appear on a statement. Once Bookkeeping advances them, they need to be pulled in.</p>
      <p><strong>Colors:</strong> Green = 0. Yellow = some waiting.</p>
      <p><strong>What to do:</strong> Click <em>Regenerate Expenses</em> on the tile. Adds them to non-Published statements only.</p>
    </div>

    <div class="tile-help">
      <h3>🚫 Expense Blockers</h3>
      <p><strong>What it means:</strong> Expenses that SHOULD be on the statement but won't appear because of a data problem — either the Statement Billed Date is missing OR the status is not REVIEWED.</p>
      <p><strong>Why this matters:</strong> These are <em>silent</em>. The owner statement won't show them unless you fix them. Common cause: Breezeway-imported expenses arrive at APPROVED — they need to be advanced to REVIEWED.</p>
      <p><strong>Colors:</strong> Green = 0. Red = problems exist.</p>
      <p><strong>What to do:</strong> Click <em>View List</em> to see the affected expenses. For "Not Reviewed" use <em>Bulk Set REVIEWED</em>. For "NULL Billed Date" open the expense and set the date manually.</p>
    </div>

    <div class="tile-help">
      <h3>🔓 Unlocked Reservations</h3>
      <p><strong>What it means:</strong> Reservations whose ledger rows are NOT locked. The system <strong>silently skips them</strong> — the entire reservation is excluded.</p>
      <p><strong>Why this happens:</strong> When a guest adds an item (rollaway, crib) on the website, or an admin adds a line on the reservation, the new ledger row used to be inserted unlocked. As of 2026-04-26 new rows auto-lock — but pre-existing unlocked rows still need cleanup.</p>
      <p><strong>Colors:</strong> Green = 0. Red = silent revenue loss for owners.</p>
      <p><strong>What to do:</strong> Click <em>Lock All in Period</em>. Then click <em>Regenerate Statements</em> on the Reservations tile.</p>
    </div>

    <div class="tile-help">
      <h3>❌ Cancelled (OX) Awaiting</h3>
      <p><strong>What it means:</strong> Cancelled bookings where KBM kept money AND the unit did NOT re-book. The owner is owed this cancellation revenue.</p>
      <p><strong>Why this matters:</strong> Cancellation revenue belongs to the owner ONLY when (1) we kept the guest's payment AND (2) we didn't re-rent the dates. Otherwise the owner has no claim, or the replacement reservation pays them.</p>
      <p><strong>Colors:</strong> Green = 0. Red = owner missing revenue.</p>
      <p><strong>What to do:</strong> Click <em>View List</em> to see the qualifying reservations and net retained amount. Manually add each one via the reservation page (Phase 2 will auto-include).</p>
    </div>

    <div class="tile-help">
      <h3>📧 Email Progress</h3>
      <p><strong>What it means:</strong> How many Published statements have been emailed to owners.</p>
      <p><strong>Colors:</strong> Gray = no Published yet. Yellow = some sent, some remaining. Green = all sent.</p>
      <p><strong>What to do:</strong> Click <em>Show Unemailed</em> to filter the list. Then use the red <em>Bulk Mail Statements</em> button at the top of the table.</p>
    </div>

    <div class="tile-help">
      <h3>💳 Payments</h3>
      <p><strong>What it means:</strong> How many statement payments have CLEARED via NACHA / ACH (or manual check / wire).</p>
      <p><strong>Colors:</strong> Red = nobody paid yet OR there are RETURNED / FAILED payments. Yellow = some paid, others still in flight (SUBMITTED, awaiting bank settlement). Green = every payable statement is PAID.</p>
      <p><strong>What to do:</strong> For ACH bulk payments — generate the NACHA Distribution file (next tile), Jacki uploads to BOH, the system auto-marks PAID after the effective date. For owners without bank info — see <a (click)="scrollTo('manual-check')">Section 7</a> below for how to handle manual check payments.</p>
    </div>

    <div class="tile-help">
      <h3>🏦 NACHA Distribution</h3>
      <p><strong>What it means:</strong> Status of the monthly NACHA batch file used to send ACH payments to all owners at once via Bank of Hawaii.</p>
      <p><strong>Colors:</strong> Red = not generated yet. Yellow = generated but not submitted (Jacki needs to upload + approve). Green = submitted to BOH.</p>
      <p><strong>What to do:</strong> Use <code>POST /api/2.6/nacha/dist-generate</code> to build the .ach file. Jacki uploads to BOH Payments Management portal and approves. After upload, mark the batch SUBMITTED so this tile turns green.</p>
    </div>
  </section>

  <!-- Section 4 -->
  <section id="statuses">
    <h2>4. Statement Statuses</h2>
    <p>Every statement moves through a status ladder:</p>
    <table class="status-table">
      <tr><td><span class="badge default">0 DEFAULT</span></td><td>Initial / unset</td></tr>
      <tr><td><span class="badge draft">1 DRAFT</span></td><td>Just generated — first state</td></tr>
      <tr><td><span class="badge need-attn">2 NEED_ATTENTION</span></td><td>Flagged for review by reviewer</td></tr>
      <tr><td><span class="badge rfr">3 RFR1</span></td><td>Ready for Review #1</td></tr>
      <tr><td><span class="badge rfr">4 RFR2</span></td><td>Ready for Review #2</td></tr>
      <tr><td><span class="badge rfr">5 RFR3</span></td><td>Ready for Review #3</td></tr>
      <tr><td><span class="badge approved">6 APPROVED</span></td><td>Approved, not yet visible to owner</td></tr>
      <tr><td><span class="badge published">7 PUBLISHED</span></td><td>Visible on owner portal — required before email — blocks all edits</td></tr>
    </table>
    <div class="callout">
      <strong>Once a statement is PUBLISHED (status 7), it is FROZEN.</strong> You cannot edit it. Corrections must be posted on the next month's statement as expenses.
    </div>
  </section>

  <!-- Section 5 -->
  <section id="regenerate">
    <h2>5. Regenerate vs Generate — Don't Confuse Them</h2>
    <table class="compare">
      <thead>
        <tr><th>Action</th><th>What it does</th><th>When to use</th></tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Generate Bulk Statement</strong></td>
          <td>Creates a NEW statement for every active property that doesn't already have one. Skips existing statements.</td>
          <td>Once per month, around the 1st-5th of the following month, to create the initial Draft batch.</td>
        </tr>
        <tr>
          <td><strong>Regenerate Bulk Statement</strong></td>
          <td>Wipes existing reservations and expenses on every non-Published statement, then re-adds everything from current data.</td>
          <td>Whenever the Dashboard shows yellow Reservations Ready or Expenses Ready. Safe — Published statements are not touched.</td>
        </tr>
        <tr>
          <td><strong>Regenerate Statement Expenses</strong></td>
          <td>Same as Regenerate but ONLY refreshes expenses (leaves reservations alone). Faster and safer when only expense data changed.</td>
          <td>When only expenses were modified (e.g., bookkeeping advanced expenses to REVIEWED).</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- Section 6 -->
  <section id="payments">
    <h2>6. Payments &amp; NACHA</h2>
    <p>Most owners are paid via <strong>ACH</strong> (NACHA file uploaded to Bank of Hawaii). Some owners require manual checks. The <em>paymentStatus</em> column on each statement tracks this:</p>
    <table class="status-table">
      <tr><td><strong>NULL</strong></td><td>Not yet evaluated</td></tr>
      <tr><td><strong>NOT_APPLICABLE</strong></td><td>Statement has $0 or negative net — no payment due</td></tr>
      <tr><td><strong>PENDING</strong></td><td>Positive net, not yet in a NACHA batch</td></tr>
      <tr><td><strong>QUEUED</strong></td><td>In a NACHA batch (generated, not yet submitted)</td></tr>
      <tr><td><strong>SUBMITTED</strong></td><td>Batch submitted to BOH, awaiting settlement (~1-2 business days)</td></tr>
      <tr><td><strong>PAID</strong></td><td>Settled by the bank</td></tr>
      <tr><td><strong>RETURNED</strong></td><td>NACHA return received (closed account, etc.) — needs follow-up</td></tr>
      <tr><td><strong>FAILED</strong></td><td>Other failure</td></tr>
      <tr><td><strong>MANUAL_PAID</strong></td><td>Paid outside NACHA (check, wire) — the manual check workflow</td></tr>
    </table>
    <p>The nightly Quartz job auto-promotes SUBMITTED statements to PAID once the effective date has passed and no return marker exists.</p>
  </section>

  <!-- Section 7 -->
  <section id="manual-check">
    <h2>7. Manual Check Payments</h2>
    <p>Some owners do not provide bank routing/account info, OR an ACH payment was returned. They need a manual check.</p>
    <ol>
      <li>The Payments tile shows them as PENDING / RETURNED / FAILED, OR they fall outside the NACHA batch entirely (no bank info on file).</li>
      <li>Cut a paper check and mail / hand-deliver to the owner.</li>
      <li>Once the check is in the owner's hands, mark the statement as paid:
        <pre>PUT /api/2.6/owner-stmts/&#123;ownerStmtId&#125;/payment-status
&#123;
  "status": "MANUAL_PAID",
  "paidDate": "2026-05-12",
  "paymentMethod": "CHECK",
  "reference": "Check #1234"
&#125;</pre>
      </li>
      <li>The Payments dashboard tile will then count it toward the green-when-everyone-paid total.</li>
    </ol>
    <div class="callout">
      The "manual mark paid" UI is on the Statement detail page (Payment Method dropdown + reference field). It is also available in the upcoming Payments Drill-Down dialog on the dashboard.
    </div>
  </section>

  <!-- Section 8 -->
  <section id="newsletter">
    <h2>8. Newsletter / CMS Letter</h2>
    <p>Every owner statement carries a monthly letter (the "Statement Letter") at the top of the PDF and inside the bulk-send email body. It is one row per development per month in the <code>str_content</code> table (contentTypeId = 2, visibility = PRIVATE).</p>

    <h3>How the lookup works</h3>
    <ol>
      <li>For each statement, the system reads the rental's <code>developmentId</code>.</li>
      <li>The system queries the CMS for a content row matching:
        <ul>
          <li><code>contentTypeId = 2</code></li>
          <li><code>status = 'PUBLISHED'</code> (or PUBLISHED+REVIEW for admin preview)</li>
          <li><code>visibility = 'PRIVATE'</code></li>
          <li><code>statementMonth = MM/yyyy</code> of the statement period (e.g. <code>04/2026</code>)</li>
          <li>The development must appear in the <strong><code>str_content_development</code> junction table</strong> for that content row.</li>
        </ul>
      </li>
      <li>If anything is missing → owner sees "Statement letter not found" on the portal and the email body has only the boilerplate.</li>
    </ol>

    <h3>Authoring a letter (Sarah's workflow)</h3>
    <ol>
      <li>Go to the CMS Statement Letters page in KBMAdmin.</li>
      <li>Click <em>New Letter</em>. Pick the <strong>statementMonth</strong> (e.g. April 2026 = <code>04/2026</code>).</li>
      <li>Set <strong>contentTypeId = Statement Letter</strong>, <strong>visibility = PRIVATE</strong>.</li>
      <li>Write the letter body. Use the editor — final body is sanitized HTML.</li>
      <li>Assign developments — every development that should receive this letter must be selected. The page writes to the <code>str_content_development</code> junction table.</li>
      <li>Save as <strong>PUBLISHED</strong>. (REVIEW is admin-preview-only — the owner portal and email body REQUIRE PUBLISHED.)</li>
    </ol>

    <div class="callout">
      <strong>Singular vs junction column warning.</strong> The old <code>str_content.developmentId</code> singular column still exists for backward compatibility, but the production lookup uses the <strong>junction table</strong>. If the CMS edit UI shows a property as "not assigned" while the email actually goes out correctly, that's the CMS UI reading the singular column. The truth is in the junction.
    </div>

    <h3>Coverage check (do this before bulk send)</h3>
    <p>Run this query to confirm every active development has a PUBLISHED letter for the target month. Any development with <code>0</code> matches will email out with boilerplate only.</p>
    <pre>SELECT d.developmentId, d.developmentName,
       COUNT(DISTINCT c.contentId) AS letterCount
FROM str_development d
LEFT JOIN str_content_development cd ON cd.developmentId = d.developmentId
LEFT JOIN str_content c ON c.contentId = cd.contentId
  AND c.contentTypeId = 2
  AND c.status = 'PUBLISHED'
  AND c.visibility = 'PRIVATE'
  AND c.statementMonth = 'MM/yyyy'   -- replace
WHERE EXISTS (SELECT 1 FROM str_short_term_rental r
              WHERE r.developmentId = d.developmentId AND r.status = 'ACTIVE')
GROUP BY d.developmentId, d.developmentName
HAVING letterCount = 0
ORDER BY d.developmentName;</pre>
  </section>

  <!-- Section 9 -->
  <section id="smoke-test">
    <h2>9. Smoke Test &amp; Bulk Email</h2>
    <p>Always send a 2-property smoke test BEFORE the full bulk run. This protects against newsletter, co-owner, attachment, and template regressions.</p>

    <h3>Required smoke-test properties</h3>
    <ul>
      <li><strong>HKK-334</strong> (Konea unit, Josh-owned). Multi-owner — verifies the "send to ALL owners" path. Owners on file: <code>joshmotto&#64;loanrepublic.com</code> + <code>sarahjenhatch&#64;gmail.com</code>.</li>
      <li><strong>HKH-236</strong> (Hoku unit, Josh-owned). Single-owner sanity check.</li>
    </ul>

    <h3>Smoke-test procedure</h3>
    <ol>
      <li>From the Statement Dashboard, filter to just those two propertyCodes.</li>
      <li>Click <em>Bulk Mail Statements</em>. Confirm with the required confirmation phrase.</li>
      <li>Open the two delivered emails in each inbox. Verify:
        <ul>
          <li>Newsletter HTML appears at the top of the body (before the boilerplate).</li>
          <li>Statement PDF is attached.</li>
          <li>For HKK-334: both owner inboxes received the message.</li>
          <li>Subject + From-name match the template.</li>
        </ul>
      </li>
      <li>Open the owner portal at <a href="https://owner.kbmresorts.com">owner.kbmresorts.com</a> and verify the same two statements render correctly (newsletter present, totals match the email).</li>
      <li>Only then run the full bulk send for the remaining ~235 statements.</li>
    </ol>

    <div class="callout">
      <strong>Co-owners.</strong> As of 2026-05-12 (commit 01f1b3f05) the bulk-send loop iterates over ALL owners on the unit, not just <code>ownerProfiles[0]</code>. The smoke test on HKK-334 is the gate that verifies this fix has shipped.
    </div>
  </section>

  <!-- Section 10 -->
  <section id="reconciliation">
    <h2>10. Wire Reconciliation</h2>
    <p>After BOH posts the ACH wire, we cross-check the Bank-side amount against the Wiki-computed "payment to owner" for every paid statement. Discrepancies happen when a statement is regenerated AFTER the NACHA file is sealed but BEFORE settlement, or when the carry-forward distribution numbers go stale.</p>

    <h3>Single source of truth</h3>
    <p>The NACHA distribution amount, the PDF "Payment to Owner", the email body total, and the bank-statement line on the owner statement MUST all match. The plumbing for this is in <code>NachaDistBL → OwnerStmtPdfBL.computePaymentToOwner → getOwnerStmtPdfAPI → StmtSummeryAPI.paymentToOwner</code> (single source of truth, commit bb38de30c). Never short-circuit this chain.</p>

    <p>Formula:</p>
    <pre>paymentToOwner = MAX(0, startingBalance + totalRevenue + totalExpense)</pre>

    <h3>Day-after reconciliation procedure (Day +1 after wire)</h3>
    <ol>
      <li>Export the BOH wire confirmation as CSV.</li>
      <li>Pull the matching statement rows from <code>str_owner_stmt</code> for the period.</li>
      <li>For every row, compare <code>BOH wire amount</code> vs the Wiki-recomputed <code>paymentToOwner</code> as of today.</li>
      <li>For mismatches:
        <ul>
          <li><strong>Underpayments</strong> (Bank &lt; Wiki) — set up a supplemental ACH today for the delta.</li>
          <li><strong>Overpayments</strong> (Bank &gt; Wiki) — recover via the NEXT month's statement using a "Tax Expense" recovery line OR a prior-month adjustment carry-forward (negative <code>startingBalance</code>).</li>
        </ul>
      </li>
      <li>Document every reconciliation entry in the homeowner-statement-manager log with: stmtId, BOH amount, Wiki amount, delta, remedy, ETA.</li>
    </ol>

    <div class="callout">
      <strong>Never defer to next month.</strong> Underpayments are paid the SAME day. Overpayments must be fully booked into the next month's stmt before that stmt is published — no rolling debits across multiple months.
    </div>

    <h3>Avoiding stale carry-forward</h3>
    <p>If you run a round of SQL fixes on existing PUBLISHED or REVIEWED statements, you MUST re-call <code>POST /api/2.6/nacha/dist-preview</code> for the affected stmtIds BEFORE sealing the NACHA file. Reusing the previous round's distribution amounts is the bug that caused the May 12 V7.2 over/underpayments.</p>
  </section>

  <!-- Section 11 -->
  <section id="tax-filing">
    <h2>11. Tax Filing (GET / TAT / MCTAT / OTAT / HCTAT)</h2>
    <p>Each month KBM remits Hawaii transient-accommodations and general-excise taxes on behalf of the owners. The taxes were COLLECTED from guests during the prior month and POOLED in the KBM tax-trust account until remittance. Divya runs this cycle.</p>

    <h3>Tax types</h3>
    <table class="status-table">
      <tr><td><strong>GET</strong></td><td>Hawaii General Excise Tax — 4.5% (Oahu) / 4.4386% (Maui &amp; Big Island combined effective rate)</td></tr>
      <tr><td><strong>TAT</strong></td><td>State Transient Accommodations Tax — 10.25%</td></tr>
      <tr><td><strong>MCTAT</strong></td><td>Maui County TAT — 3% (Maui properties only)</td></tr>
      <tr><td><strong>OTAT</strong></td><td>Oahu County TAT — 3% (Oahu properties only)</td></tr>
      <tr><td><strong>HCTAT</strong></td><td>Hawaii County TAT — 3% (Big Island properties only)</td></tr>
      <tr><td><strong>UT-TRT</strong></td><td>Utah Transient Room Tax — Park City properties only — separate filing</td></tr>
    </table>

    <h3>Monthly tax-filing calendar</h3>
    <table class="calendar-table">
      <thead>
        <tr><th>When</th><th>Who</th><th>What</th></tr>
      </thead>
      <tbody>
        <tr><td>Day 1-12</td><td>System</td><td>Tax is collected at check-in. The collected amount appears as a Tax line on every reservation; the offset is a liability accrual to the tax-trust account.</td></tr>
        <tr><td>Day 12</td><td>Bookkeeping</td><td>Owner-statement payment goes out. Tax has been withheld and held in the tax-trust account — this stays separate from the owner ACH wire.</td></tr>
        <tr><td>Day 13-15</td><td>Divya</td><td>Reconcile collected tax for the prior calendar month. Pull the tax-collected report by tax type (GET, TAT, MCTAT, OTAT, HCTAT). Spot-check tax = rate × taxable revenue.</td></tr>
        <tr><td>Day 16-18</td><td>Divya</td><td>Generate the <strong>Tax NACHA</strong> file. One ACH batch per tax type. Build the supporting reconciliation CSV (one row per property, columns: GET, TAT, MCTAT/OTAT/HCTAT).</td></tr>
        <tr><td>Day 18-19</td><td>Josh / Divya</td><td>Review reconciliation. Approve.</td></tr>
        <tr><td>Day 19</td><td>Divya</td><td>Upload Tax NACHA to BOH Payments Management. Jacki approves in BOH portal.</td></tr>
        <tr><td><strong>Day 20</strong></td><td>BOH / DOTAX</td><td><strong>Hard deadline.</strong> Hawaii DOTAX requires GET/TAT/MCTAT remittance by the 20th of the month for the prior month. File the periodic returns electronically via Hawaii Tax Online at the same time the ACH posts.</td></tr>
        <tr><td>Day 20+</td><td>Bookkeeping</td><td>Mark the tax-trust journal entries cleared. Reset the running monthly tax accrual to zero for the new period.</td></tr>
      </tbody>
    </table>

    <h3>How to generate the Tax NACHA</h3>
    <ol>
      <li>Endpoint: <code>POST /api/2.6/nacha/tax-generate</code> with body <code>{{ '{' }} "year": 2026, "month": 4, "taxTypes": ["GET","TAT","MCTAT","OTAT","HCTAT"] {{ '}' }}</code>.</li>
      <li>The job:
        <ul>
          <li>Queries <code>str_reservation_ledger</code> for tax rows with <code>billedDate</code> in the target month and <code>locked = 1</code>.</li>
          <li>Aggregates per tax type at the entity level (KBM HI Trust). Maps each property to the correct county for MCTAT/OTAT/HCTAT.</li>
          <li>Emits one NACHA file per tax type. PPD class 200 (payment to State of Hawaii DOTAX bank account on file).</li>
          <li>Writes a reconciliation CSV: per-property + per-tax-type breakdown.</li>
        </ul>
      </li>
      <li>The job emails Sarah + Divya + Josh with: the NACHA file(s), the reconciliation CSV, the total to remit per tax type, and any "omitted property" list (properties with collected tax that didn't make it onto the file — usually because their reservations weren't locked).</li>
    </ol>

    <h3>Filing the returns at Hawaii Tax Online</h3>
    <ol>
      <li>Log into Hawaii Tax Online with the KBM HI Trust credentials.</li>
      <li>File the <strong>G-45 periodic GET return</strong> for the prior month (must match the GET amount on the NACHA).</li>
      <li>File the <strong>TA-1 periodic TAT return</strong> for the prior month (must match TAT + MCTAT/OTAT/HCTAT).</li>
      <li>Confirmation numbers from both filings go into the homeowner-statement-manager log for that month.</li>
    </ol>

    <h3>Annual tax filings</h3>
    <table class="status-table">
      <tr><td><strong>G-49 GET annual</strong></td><td>Due April 20 of the following year. Reconciles all G-45 periodic returns.</td></tr>
      <tr><td><strong>TA-2 TAT annual</strong></td><td>Due April 20 of the following year. Reconciles all TA-1 periodic returns.</td></tr>
      <tr><td><strong>1099-MISC / 1099-NEC</strong></td><td>Issued to owners + contractors by Jan 31. tax-agent owns these.</td></tr>
      <tr><td><strong>1042-S</strong></td><td>Non-resident owner withholding. Issued by Mar 15.</td></tr>
    </table>

    <h3>Omitted properties</h3>
    <p>If a property collected tax but the Tax NACHA <em>excluded</em> it (e.g. reservations were not locked at the time of generation), the omission is listed in the reconciliation CSV. These must be added in a supplemental Tax NACHA filed before the 20th. Never skip; never roll into the next month.</p>

    <h3>Utah tax filing</h3>
    <p>Park City properties pay Utah TRT (Transient Room Tax) directly to the Utah State Tax Commission. This is a separate ACH and a separate return (Utah TC-62T form). Smaller dollar amounts, but same monthly cadence and same 20th-of-the-month deadline pattern.</p>

    <div class="callout">
      <strong>The Tax NACHA is OWNED by Divya.</strong> Owner-side ACH (the wire that pays homeowners) is Jacki. The two never share a NACHA file — they are separate batches submitted to separate BOH accounts.
    </div>
  </section>

  <!-- Section 12 -->
  <section id="troubleshooting">
    <h2>12. Troubleshooting</h2>

    <div class="trouble">
      <h3>"My statement is missing a reservation that should be there"</h3>
      <ol>
        <li>Look at the Dashboard's <strong>Unlocked Reservations</strong> tile. If &gt; 0, click <em>Lock All in Period</em>, then click <em>Regenerate Statements</em>.</li>
        <li>Look at <strong>Reservations Ready to Add</strong>. If &gt; 0, click <em>Regenerate Statements</em>.</li>
        <li>If the reservation is <strong>cancelled (OX)</strong>: check <em>Cancelled (OX) Awaiting</em>. If it's there, manually add it.</li>
      </ol>
    </div>

    <div class="trouble">
      <h3>"My statement is missing an expense that should be there"</h3>
      <ol>
        <li>Look at <strong>Expense Blockers</strong>. If &gt; 0, click <em>View List</em> — the expense is probably APPROVED-not-REVIEWED or has a NULL billed date.</li>
        <li>Click <em>Bulk Set REVIEWED</em> for the not-REVIEWED ones. Open and edit the NULL date ones.</li>
        <li>Then click <em>Regenerate Expenses</em> on the Expenses tile.</li>
      </ol>
    </div>

    <div class="trouble">
      <h3>"The Dashboard says 0 paid but everyone was paid"</h3>
      <p>The <code>paymentStatus</code> column may not have been backfilled yet. Run the backfill SQL or the Quartz auto-promote job to update it from the NACHA batch records. See the homeowner-statement-manager agent doc.</p>
    </div>

    <div class="trouble">
      <h3>"I clicked Regenerate but a Published statement didn't update"</h3>
      <p>This is intentional. Once Published, statements are frozen. Corrections must go on next month's statement as a new expense line.</p>
    </div>

    <div class="trouble">
      <h3>"An owner's check bounced / NACHA was returned"</h3>
      <p>Mark the statement as <strong>RETURNED</strong> via the manual-mark endpoint. Cut a new check or coordinate a new ACH attempt. After the new payment clears, mark the statement <strong>MANUAL_PAID</strong> or <strong>PAID</strong>.</p>
    </div>
  </section>

  <!-- Section 13 -->
  <section id="glossary">
    <h2>13. Glossary</h2>
    <dl>
      <dt>ACH</dt><dd>Automated Clearing House. The bank network used for electronic payments to owners.</dd>
      <dt>Breezeway</dt><dd>KBM's field operations platform. Imports daily into str_expense as Approved expenses.</dd>
      <dt>NACHA</dt><dd>The standardized file format used to upload bulk ACH payments to Bank of Hawaii.</dd>
      <dt>Net Retained</dt><dd>For a cancelled reservation: payments received minus refunds issued. Owner is owed this when &gt; 0 and unit didn't re-book.</dd>
      <dt>OX</dt><dd>Reservation status code for "Sale Cancelled."</dd>
      <dt>regenerateOption</dt><dd>The lock flag on every str_reservation_ledger row. 1 = locked (eligible for statement). 0 / NULL = unlocked (silently excluded).</dd>
      <dt>REVIEWED</dt><dd>Expense status 6. Only REVIEWED expenses appear on owner statements.</dd>
      <dt>showToStmt</dt><dd>Boolean flag on str_expense. Must be true (1) for the expense to even be considered for a statement.</dd>
      <dt>stmtBilledDate</dt><dd>The date used to determine which statement period an expense belongs to. Decoupled from the actual expense date.</dd>
      <dt>totalNetProceed</dt><dd>The bottom-line amount owed to the owner on the statement.</dd>
    </dl>
  </section>

  <footer class="guide-footer">
    <p>Questions? Slack #accounting or email <a href="mailto:josh&#64;kbmresorts.com">josh&#64;kbmresorts.com</a>. This guide is maintained alongside the homeowner-statement-manager agent at <code>ClaudeTeamLocal/.claude/agents/homeowner-statement-manager.md</code>.</p>
  </footer>
</div>
